import './style.scss';

import axios from 'axios';
import { ContentState, convertToRaw, EditorState } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { Editor } from 'react-draft-wysiwyg';
import { NumericFormat as NumberFormat } from 'react-number-format';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import AsyncSelect from 'react-select/async-creatable';
import Select from 'react-select/creatable';
import { toast as Toast } from 'react-toastify';
import { Alert, Button, Card, Modal } from 'reactstrap';
import { z } from 'zod';

import Breadcrumb from '@/components/common/breadcrumb.component';
import Dropzone from '@/components/dropzone';
import Icon from '@/components/icon';
import ImageImportModal from '@/components/image-import-modal';
import ModalDialog from '@/components/modal-dialog';
import ToastContent from '@/components/toasts/content';
import { Constants } from '@/constants';

const requiredText = (message: string) =>
  z.preprocess(
    (val) => (val == null ? '' : val), // undefined/null -> ""
    z.string().trim().min(1, message)
  );

const optionalText = () =>
  z.preprocess(
    (val) => (val == null ? '' : val), // undefined/null -> ""
    z.string().trim().optional()
  );

const requiredNumber = (message: string) =>
  z.preprocess(
    (val) => {
      if (val === '' || val == null) return null; // normalize empty
      const num = Number(val);
      return Number.isFinite(num) ? num : null;
    },
    z
      .number()
      .or(z.null())
      .superRefine((val, ctx) => {
        if (val === null) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message,
          });
          return;
        }
        if (val < 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message,
          });
        }
      })
  );

const optionalNumber = () =>
  z.preprocess((val) => {
    if (val === '' || val == null) return undefined;
    const num = Number(val);
    return Number.isFinite(num) ? num : undefined;
  }, z.number().min(0).optional());

const productSchema = z.object({
  id: z.number().optional(),
  venue_id: z.number('Shop is required'),

  name: requiredText("Please include the product's name"),
  description: optionalText(),
  reference: requiredText('Product reference / SKU is required'),
  buy_url: optionalText(),
  upc: optionalText(),
  sale_price: requiredNumber('Please include a valid price'),
  price: optionalNumber(),
  cost_price: optionalNumber(),
  availability: requiredText('Availability is required'),
  packaging: z
    .object({
      type: z.string().min(1, 'Packaging type is required'),
      // INFO Validation handled below in superRefine
      quantity: z.coerce.number().optional(),
    })
    .optional()
    .superRefine((packaging, ctx) => {
      if (!packaging) return;

      const type = packaging.type?.trim().toLowerCase();
      console.log('type:', type);

      // Samplers and accessories do not require quantity
      if (
        type !== 'sampler' &&
        type !== 'accessory' &&
        type !== 'subscription' &&
        packaging.quantity === 0
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['quantity'],
          message: 'Packaging quantity should be greater than 0',
        });
      }
    }),

  images: z.array(z.any()).optional(),
  contents: z.array(z.any()).optional(),

  active: z.boolean().optional(),
  out_of_stock: z.boolean().optional(),
  stock_count: optionalNumber(),
});

const toErrorMap = (error: z.ZodError) => {
  const fieldErrors: Record<string, string> = {};

  for (const issue of error.issues) {
    const field = issue.path.join('.'); // handles nested like "address.street"
    // only set the first error per field, or change this to push into an array
    if (!fieldErrors[field]) {
      fieldErrors[field] = issue.message;
    }
  }

  return fieldErrors;
};

const defaultPackageType = [
  'Single',
  'Pack',
  'Box',
  'Bundle',
  'Jar',
  'Sampler',
  'Tin',
  'Accessory',
  'Subscription',
];
const defaultCategories = [
  // "All", // Always added to this one server-side, don't need to pick it
  'Cigar Aficionado Top 25', // These two will dynamically add it to the current year's category
  'IPCPR/PCA New Releases',
];

type ValidationErrors = Record<string, string>;
type ValidationWarnings = Record<string, string>;

const ProductEditor = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();

  const [selectedName, setSelectedName] = React.useState('');

  const [state, setState] = React.useState({
    selectedProduct: {
      name: '',
      description: '',
      buy_url: '',
      reference: '',
      upc: '',
      msrp: '',
      sale_price: '',
      cost_price: '',
      stock_count: '',
      contents: [],
    },
    selectedProductImages: [],
    selectedImagesToAttach: [],
    selectedImagesToRemove: [],
    selectedAvailability: null,
    selectedVenue: null,
    selectedPackageType: null,
    selectedPackageQuantity: '',
    selectedCategories: [],
    cigarSuggestions: [],
    vitolaOptions: {},
    productSuggestions: [],
    selectedCigarSuggestion: 0,
    linkedCigars: [],
  });

  const [editorState, setEditorState] = React.useState(null);
  const [showImageUrlModal, setShowImageUrlModal] = useState(false);
  const [showLinkCigarModal, setShowLinkCigarModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [hasId, setHasId] = useState(false);
  const [hasData, setHasData] = useState(false);
  const [defaultCigars, setDefaultCigars] = useState([]);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>(
    {}
  );

  const productImages = useRef(null);

  const updateState = (newState: any) =>
    setState((prev: any) => ({ ...prev, ...newState }));

  const handleProductChange = (field) => (e) => {
    setState((prev) => ({
      ...prev,
      selectedProduct: {
        ...prev.selectedProduct,
        [field]: e.target.value,
      },
    }));
  };

  const handleInputChange = (field) => (e) => {
    setState((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSelectChange = (field) => (value) => {
    setState((prev) => ({ ...prev, [field]: value }));
  };

  const formatVenueLabel = (venue) => {
    console.log(venue);
    let label = venue.name;
    if (venue.location.formatted_address) {
      label += ` - ${venue.location.formatted_address}`;
    } else if (venue.website) {
      label += ` - ${venue.website}`;
    }
    return label;
  };

  const populateForm = (product) => {
    setState((prev) => ({
      ...prev,
      selectedProduct: product,
      selectedProductImages: product.images || [],
    }));
    setSelectedName(product.name || '');
    // getCigarSuggestions(product.name);
    // getProductSuggestions(product.reference);

    const categories = [];
    let packagingType = null;
    let packagingQuantity = 1;

    if (product.description) {
      const contentBlock = htmlToDraft(product.description);
      if (contentBlock) {
        const { contentBlocks, entityMap } = contentBlock;
        const contentState = ContentState.createFromBlockArray(
          contentBlocks,
          entityMap
        );
        setEditorState(EditorState.createWithContent(contentState));
      }
    } else {
      setEditorState(EditorState.createEmpty());
    }

    if (product.categories) {
      for (let i = 0; i < product.categories.length; i++) {
        const category = product.categories[i];
        categories.push({
          value: category,
          label: category,
        });
      }
    }

    if (product.packaging) {
      packagingType = {
        label: product.packaging.type,
        value: product.packaging.type,
      };
      packagingQuantity = product.packaging.quantity;
    }

    if (
      product.price &&
      typeof product.price === 'number' &&
      product.price > 0
    ) {
      product.price = product.price.toFixed(2);
    }

    updateState({
      editorState,
      selectedProduct: product,
      // productImages: product.image_url,
      selectedProductImages: product.images,
      selectedAvailability: product.availability
        ? {
            value: product.availability,
            label: product.availability,
          }
        : '',
      selectedCategories: categories,
      selectedPackageType: packagingType,
      selectedPackageQuantity: packagingQuantity,
      selectedVenue: product.venue
        ? {
            value: product.venue.id,
            label: formatVenueLabel(product.venue),
          }
        : null,
    });
  };

  useEffect(() => {
    if (searchParams?.has('import')) {
      // TODO Pull this code from the ProductManager
      // setShowImportModal(true);
    }
  }, [searchParams]);

  useEffect(() => {
    // TODO: If no details are passed in the route and the id param is not "new", fetch product details.
    if (
      !location.state?.product ||
      (params.id &&
        params.id !== location.state?.product.hash_id &&
        params.id !== 'new')
    ) {
      // getProductById(hashids.decode(params.id));
    }
    setHasId(params.id !== 'new');
  }, [params.id]);

  useEffect(() => {
    if (location.state?.product) {
      const { product } = location.state;
      populateForm(product);
    }
  }, [location.state?.product]);

  const saveProduct = () => {
    const { selectedProduct } = state;
    if (!selectedProduct) return;

    const {
      selectedVenue,
      selectedPackageType,
      selectedPackageQuantity,
      selectedCategories,
      selectedAvailability,
      selectedImagesToAttach,
      selectedImagesToRemove,
    } = state;

    const updatedProduct: any = {
      ...selectedProduct,
    };

    const contentState = editorState.getCurrentContent();
    updatedProduct.description = draftToHtml(convertToRaw(contentState)); // TODO Sanitize?

    if (selectedVenue?.value) {
      updatedProduct.venue_id = selectedVenue.value;
    }

    const hasPackageType = !!selectedPackageType?.value;
    const hasPackageQty =
      selectedPackageQuantity !== '' && selectedPackageQuantity != null;

    if (hasPackageType || hasPackageQty) {
      const qtyNum = Number(selectedPackageQuantity);
      updatedProduct.packaging = {
        type: hasPackageType ? selectedPackageType.value : '',
        quantity: hasPackageQty && Number.isFinite(qtyNum) ? qtyNum : 1,
      };
    }

    if (Array.isArray(selectedCategories) && selectedCategories.length) {
      updatedProduct.category_ids = selectedCategories.map((c: any) =>
        typeof c === 'object' ? c.id : c
      );
    }

    if (selectedAvailability?.value) {
      updatedProduct.availability = selectedAvailability.value;
      updatedProduct.out_of_stock = selectedAvailability.value !== 'In Stock';
    }

    // FIXME This is its own endpoint, so handle after the product is saved
    console.log(updatedProduct.contents);

    // Do we need to pass these at all?
    // const currentImages = Array.isArray(selectedProduct.images)
    //   ? selectedProduct.images
    //   : [];

    const imagesToAttach = Array.isArray(selectedImagesToAttach)
      ? selectedImagesToAttach
      : [];

    const imagesToRemove = Array.isArray(selectedImagesToRemove)
      ? selectedImagesToRemove
      : [];

    const media = {
      add: imagesToAttach,
      remove: imagesToRemove,
    };

    console.log('Saving product:', updatedProduct);

    const result = productSchema.safeParse(updatedProduct);
    if (result.success) {
      setValidationErrors({});
      const validProduct = result.data;
      console.log('Saving product:', validProduct);
      const baseUrl = `${Constants.apiPath}/shop/products`;
      const request = updatedProduct.id
        ? axios.put(`${baseUrl}/${updatedProduct.id}`, validProduct)
        : axios.post(baseUrl, validProduct);

      request
        .then((res) => {
          populateForm({
            ...updatedProduct,
            id: res.data.id,
            hash_id: res.data.hash_id,
          });

          const promises = [];
          if (media.add.length || media.remove.length) {
            promises.push(axios.post(`${baseUrl}/${res.data.id}/media`, media));
          }

          if (updatedProduct.contents) {
            promises.push(
              axios.post(
                `${baseUrl}/${res.data.id}/cigars`,
                updatedProduct.contents
              )
            );
          }

          Promise.all(promises).then(() => {
            Toast.success(
              <ToastContent
                message="Product updated successfully"
                title={updatedProduct.name}
              />,
              {
                position: 'top-right',
              }
            );
            navigate(`/shop/editor/${res.data.hash_id}`, { replace: true });
            axios
              .get(`${baseUrl}/${updatedProduct.id}`)
              .then((productRes) => {
                populateForm(productRes.data);
              })
              .catch((err) => {
                console.error(err);
              });
          });
        })
        .catch((err) => {
          console.error(err);
          Toast.error(
            <ToastContent
              message="Unable to update product"
              title={updatedProduct.name}
            />,
            {
              position: 'top-right',
            }
          );
        });
    } else {
      console.error('Validation result:', result);
      setValidationErrors(toErrorMap(result.error));
      Toast.error(
        <ToastContent
          message="Please fix the validation errors before saving"
          title={updatedProduct.name}
        />,
        {
          position: 'top-right',
        }
      );
    }
  };

  const getBadgeTitle = useCallback(() => {
    if (hasId) {
      return state.selectedAvailability?.label;
    }
    if (hasData) {
      return 'Draft';
    }
    return null;
  }, [hasId, hasData]);

  const getBadgeColor = useCallback(() => {
    if (hasId) {
      if (state.selectedAvailability?.label === 'In Stock') {
        return 'success';
      }
      if (state.selectedAvailability?.label === 'Out Of Stock') {
        return 'danger';
      }
    }
    return 'secondary';
  }, [hasId, hasData]);

  const loadShops = (searchTerm, callback) => {
    console.log(`${Constants.apiPath}/venues?q=${searchTerm}`);
    return axios
      .get(`${Constants.apiPath}/venues?q=${searchTerm}`)
      .then((res) => {
        console.log(res.data);
        callback(
          res.data.map((venue) => ({
            value: venue.id,
            label: venue.name,
          }))
        );
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const loadCigars = (searchTerm, callback) => {
    console.log(`${Constants.apiPath}/cigars?q=${searchTerm}`);
    if (searchTerm.length > 3) {
      return axios
        .get(`${Constants.apiPath}/cigars?q=${searchTerm}`)
        .then((res) => {
          // console.log(res.data);
          const cigars = res.data.map((cigar) => ({
            value: cigar.id,
            label: cigar.full_name,
            vitolas: cigar.vitolas,
          }));

          console.log('cigars:', cigars);
          setDefaultCigars(cigars);

          callback(cigars);
        })
        .catch((err) => {
          console.error(err);
        });
    }
  };

  const handleLinkedCigarChange = (index, type) => {
    const { linkedCigars } = state;
    const linkedCigar = linkedCigars[index] || {};
    return (option) => {
      console.log('index:', index);
      console.log('type:', type);
      console.log(option);
      linkedCigar[type] = option;
      linkedCigars[index] = linkedCigar;

      console.log(linkedCigars);
      updateState({ linkedCigars });

      if (type === 'cigar') {
        if (option.vitolas && option.vitolas.length) {
          const vitolas = option.vitolas.map((vitola) => ({
            value: vitola.id,
            label: vitola.formatted_name,
          }));
          console.log('vitolas:', vitolas);
          const options = { ...state.vitolaOptions };
          options[index] = vitolas;
          updateState({
            vitolaOptions: options,
          });
        }
      }
    };
  };

  const handleLinkedCigarInputChange = (index, type) => {
    const { linkedCigars } = state;
    // console.log(linkedCigars);
    const linkedCigar = linkedCigars[index] || {};
    return (event) => {
      linkedCigar[type] = event.target.value;
      linkedCigars[index] = linkedCigar;
      updateState({ linkedCigars });
    };
  };

  const renderImageFromUrlModal = () => (
    <ImageImportModal
      type="products"
      open={showImageUrlModal}
      onClose={() => setShowImageUrlModal(false)}
      onResult={(media) => {
        console.log('Image imported successfully');
        console.log(media);
        const images = [...state.selectedProductImages];
        const imagesToAdd = [...state.selectedImagesToAttach];
        images.push({
          id: media.id,
          url: media.media_url,
        });
        imagesToAdd.push(media.media_url);
        updateState({
          selectedProductImages: images,
          selectedImagesToAttach: imagesToAdd,
        });
        setShowImageUrlModal(false);
      }}
    />
  );

  const renderLinkedCigarsRow = (type, index) => {
    console.debug('Linked cigars row:');
    console.debug(state.linkedCigars);
    console.debug(`Index: ${index}`);
    const linkDetails = state.linkedCigars[index] || {};
    if (type === 'Sampler') {
      return (
        <div
          className="row"
          style={{
            marginLeft: linkDetails.cigar ? 10 : 0,
            marginRight: linkDetails.cigar ? 10 : 0,
          }}
        >
          <div
            className={linkDetails.cigar ? 'col-md-5' : 'col-md-12'}
            style={{ padding: 0 }}
          >
            <span>{linkDetails.cigar ? 'Cigar' : 'Search Cigar To Link'}</span>
            <AsyncSelect
              value={linkDetails.cigar}
              onChange={handleLinkedCigarChange(index, 'cigar')}
              loadOptions={loadCigars.bind(this)}
              defaultOptions={
                state.cigarSuggestions?.length
                  ? state.cigarSuggestions
                  : defaultCigars
              }
              isOptionSelected={(option) => linkDetails.cigar === option}
            />
          </div>
          {linkDetails.cigar && (
            <div className="col-md-4">
              <span>Vitola</span>
              <Select
                value={linkDetails.vitola}
                onChange={handleLinkedCigarChange(index, 'vitola')}
                options={
                  state.vitolaOptions[index]?.length
                    ? state.vitolaOptions[index]
                    : []
                }
                isOptionSelected={(option) => linkDetails.vitola === option}
              />
            </div>
          )}
          {linkDetails.cigar && (
            <div className="col-md-2">
              <span>Quantity</span>
              <input
                className="form-control"
                value={linkDetails.quantity || 1}
                type="number"
                onChange={handleLinkedCigarInputChange(index, 'quantity')}
              />
            </div>
          )}
          {linkDetails.cigar && (
            <div
              className="col-md-1"
              style={{
                lineHeight: '60px',
                marginTop: 8,
              }}
            >
              <Icon
                name="trash"
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  // TODO Remove the linkDetails from state.linkedCigars (or a clone to only apply on Save)
                  const { linkedCigars } = state;
                  linkedCigars.splice(index, 1);
                  updateState({ linkedCigars });
                }}
              />
            </div>
          )}
        </div>
      );
    }
    return (
      <div className="row">
        <div className="col-md-6">
          <span>Cigar</span>
          <AsyncSelect
            value={linkDetails.cigar}
            onChange={handleLinkedCigarChange(index, 'cigar')}
            loadOptions={loadCigars.bind(this)}
            defaultOptions={
              state.cigarSuggestions?.length
                ? state.cigarSuggestions
                : defaultCigars
            }
            isOptionSelected={(option) => linkDetails.cigar === option}
          />
        </div>
        <div className="col-md-6">
          <span>Vitola</span>
          <Select
            value={linkDetails.vitola}
            onChange={handleLinkedCigarChange(index, 'vitola')}
            options={
              state.vitolaOptions[index]?.length
                ? state.vitolaOptions[index]
                : []
            }
            isOptionSelected={(option) => linkDetails.vitola === option}
          />
        </div>
      </div>
    );
  };

  const renderLinkedCigars = () => {
    if (state.selectedPackageType) {
      if (
        state.selectedPackageType.value === 'Sampler' ||
        state.selectedPackageType.value === 'Subscription'
      ) {
        const linkedCigars = [...state.linkedCigars];
        linkedCigars.push({}); // Adds an extra empty object to the end FIXME Should only be in the editor
        return (
          <div className="linked-cigar-block">
            {linkedCigars.map((cigar, index) =>
              renderLinkedCigarsRow(state.selectedPackageType.value, index)
            )}
            {/* <button type="button" className="btn btn-outline-primary" onClick={() => {}}>Add Another</button> */}
          </div>
        );
      }
      return (
        <div>{renderLinkedCigarsRow(state.selectedPackageType.value, 0)}</div>
      );
    }
    return (
      <div>
        <span>Please set the product package type before linking cigars</span>
        <AsyncSelect
          value={state.selectedPackageType}
          onChange={handleSelectChange('selectedPackageType')}
          defaultOptions={defaultPackageType.map((value) => ({
            value,
            label: value,
          }))}
          isOptionSelected={(option) => state.selectedPackageType === option}
        />
      </div>
    );
  };

  const renderLinkCigarModal = () => (
    <Modal
      isOpen={showLinkCigarModal}
      onClosed={() => setShowLinkCigarModal(false)}
      // toggle={() => setShowLinkCigarModal(false)}
      size="lg"
    >
      <div className="modal-header">
        <h5 className="modal-title">
          {`Link cigars${state.selectedProduct.name && ` to ${state.selectedProduct.name}`}`}
        </h5>
      </div>
      <div className="modal-body">{renderLinkedCigars()}</div>
      <div className="modal-footer">
        <Button onClick={() => setShowLinkCigarModal(false)}>{'Cancel'}</Button>
        <Button
          disabled={submitting}
          onClick={() => {
            const { linkedCigars, selectedProduct } = state;
            selectedProduct.contents = linkedCigars.map((item) => ({
              cigar_id: item.cigar.value,
              cigar: {
                id: item.cigar.value,
                full_name: item.cigar.label,
              },
              vitola_id: item.vitola?.value || undefined,
              vitola: item.vitola
                ? {
                    id: item.vitola.value,
                    formatted_name: item.vitola.label,
                  }
                : undefined,
              quantity: item.quantity || 1,
            }));
            updateState({ selectedProduct });
            setShowLinkCigarModal(false);
          }}
          variant="contained"
          color="primary"
        >
          {'Set'}
        </Button>
      </div>
    </Modal>
  );

  return (
    <>
      <Breadcrumb
        title={selectedName || 'Add New Product'}
        label="Editor"
        parent="Products"
        badge={getBadgeTitle()}
        badgeColor={getBadgeColor()}
      />

      <div style={{ display: 'flex' }}>
        <div style={{ flex: 1 }} />
        <button
          className="btn btn-primary"
          onClick={saveProduct}
          style={isMobile ? { padding: '6px 14px' } : {}}
        >
          {isMobile ? <i className="icon icon-plus" /> : 'Save'}
        </button>
      </div>

      <div
        className="product-editor row"
        style={{ maxWidth: 1200, margin: '24px auto' }}
      >
        {state.selectedProduct.status === 'error' && (
          <Alert
            color="danger"
            style={{ margin: 12, width: 'calc(100% - 24px)' }}
          >
            <h6>Errors preventing product from showing in listings</h6>
            <ul style={{ marginBottom: 0 }}>
              {state.selectedProduct.status_message.map((message, index) => (
                <li className="text-sm" key={index}>
                  {message}
                </li>
              ))}
            </ul>
          </Alert>
        )}

        {state.selectedProduct.status === 'warn' && (
          <Alert
            color="warning"
            style={{ margin: 12, width: 'calc(100% - 24px)' }}
          >
            <h6>Product listing warnings</h6>
            <ul style={{ marginBottom: 0 }}>
              {state.selectedProduct.status_message.map((message, index) => (
                <li className="text-sm" key={index}>
                  {message}
                </li>
              ))}
            </ul>
          </Alert>
        )}

        <div className="col-md-8">
          <Card style={{ padding: 10 }}>
            <div className="row">
              <div className="col-sm-12">
                <span style={{ fontWeight: 700 }}>Title</span>
                <div style={{ marginTop: 10, marginBottom: 10 }}>
                  <input
                    className="form-control"
                    value={state.selectedProduct.name}
                    onChange={handleProductChange('name')}
                  />
                  <span className="text-danger text-sm-start">
                    {validationErrors.name}
                  </span>
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-sm-12">
                <span style={{ fontWeight: 700 }}>Description</span>
                <div style={{ marginTop: 10, marginBottom: 10 }}>
                  <Editor
                    editorState={editorState}
                    wrapperClassName="editor-wrapper"
                    editorClassName="form-control"
                    onEditorStateChange={setEditorState}
                  />
                  <span className="text-warning text-sm-start">
                    {validationErrors.description}
                  </span>
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-sm-12">
                <span style={{ fontWeight: 700 }}>External Purchase URL</span>
                <div style={{ marginTop: 10, marginBottom: 10 }}>
                  <input
                    className="form-control"
                    value={state.selectedProduct.buy_url}
                    onChange={handleProductChange('buy_url')}
                  />
                  <span className="text-warning text-sm-start">
                    {validationErrors.buy_url}
                  </span>
                </div>
              </div>
            </div>
          </Card>
          <Card style={{ marginTop: 12, padding: 10 }}>
            <div className="row" style={{ display: 'flex' }}>
              <div className="col-md-12">
                <div>
                  <span style={{ fontWeight: 700 }}>Images</span>
                  <span
                    style={{
                      color: 'rgb(211, 169, 102)',
                      cursor: 'pointer',
                      float: 'right',
                    }}
                    onClick={() => setShowImageUrlModal(true)}
                  >
                    Add image from URL
                  </span>
                </div>
                <div className="row" style={{ padding: 20 }}>
                  {state.selectedProductImages &&
                    state.selectedProductImages.map((image) => (
                      <div
                        key={image.id}
                        className="col-md-3"
                        style={{
                          marginTop: 10,
                          marginBottom: 10,
                          cursor: 'pointer',
                        }}
                      >
                        <div className="image-overlay">
                          <Icon
                            name="trash"
                            className="delete-image-icon"
                            onClick={() => {
                              console.log('Clicked delete icon', image);
                              ModalDialog.show({
                                title: 'Delete image?',
                                message:
                                  'Are you sure you want to delete this image?',
                                type: 'error',
                                buttons: [
                                  {
                                    label: 'Cancel',
                                    onClick: () => ModalDialog.close(),
                                  },
                                  {
                                    label: 'Delete',
                                    style: { color: 'rgb(239, 81, 100)' },
                                    onClick: () => {
                                      const images = [
                                        ...state.selectedProductImages,
                                      ];
                                      const imagesToRemove = [
                                        ...state.selectedImagesToRemove,
                                      ];
                                      const index = images.findIndex(
                                        (img) => img.id === image.id
                                      );
                                      if (index > -1) {
                                        images.splice(index, 1);
                                        imagesToRemove.push(image);
                                        setState((prev) => ({
                                          ...prev,
                                          selectedProductImages: images,
                                          selectedImagesToRemove:
                                            imagesToRemove,
                                        }));
                                      }
                                      ModalDialog.close();
                                    },
                                  },
                                ],
                              });
                            }}
                          />
                          <Icon
                            name="maximize2"
                            className="expand-icon"
                            onClick={() => alert('Coming soon!')}
                          />
                        </div>
                        <img
                          src={image.url}
                          style={{
                            width: '100%',
                            borderRadius: 5,
                            border: '1px solid #dfdfdf',
                          }}
                        />
                      </div>
                    ))}
                  <div className="product-images">
                    <Dropzone
                      hideIcon
                      id="productImages"
                      ref={productImages}
                      files={undefined}
                      dropText="Add images or drop here to upload"
                      style={{ height: 150, width: 150, padding: 0 }}
                      imagePreviewStyle={{
                        height: 146,
                        width: 146,
                        objectFit: 'contain',
                      }}
                      // onDrop={onFileDrop('cigarImageFull')}
                    />
                  </div>
                </div>
              </div>
            </div>
          </Card>
          <Card style={{ marginTop: 12, padding: 10 }}>
            <div className="row">
              <div className="col-sm-12">
                <span style={{ fontWeight: 700 }}>Reference (SKU)</span>
                <div style={{ marginTop: 10, marginBottom: 10 }}>
                  <input
                    className="form-control"
                    value={state.selectedProduct.reference}
                    onChange={handleProductChange('reference')}
                  />
                  <span className="text-danger text-sm-start">
                    {validationErrors.reference}
                  </span>
                </div>
                <p style={{ fontSize: 12 }}>
                  The SKU or other reference identifying the product
                </p>
              </div>
            </div>
            <div className="row">
              <div className="col-sm-12">
                <span style={{ fontWeight: 700 }}>Barcode (UPC)</span>
                <div style={{ marginTop: 10, marginBottom: 10 }}>
                  <input
                    className="form-control"
                    value={state.selectedProduct.upc}
                    onChange={handleProductChange('upc')}
                  />
                </div>
                <p style={{ fontSize: 12 }}>
                  The UPC/EAN13 barcode for the product
                </p>
              </div>
            </div>
          </Card>
          <Card style={{ marginTop: 12, padding: 10 }}>
            <div className="row">
              <div className="col-sm-4">
                <span style={{ fontWeight: 700 }}>MSRP</span>
                <div style={{ marginTop: 10, marginBottom: 10 }}>
                  <div className="input-group">
                    <span className="input-group-text">$</span>
                    <NumberFormat
                      className="form-control"
                      thousandsGroupStyle="thousand"
                      value={state.selectedProduct.msrp}
                      decimalSeparator="."
                      displayType="input"
                      type="text"
                      thousandSeparator
                      allowNegative={false}
                      decimalScale={2}
                      fixedDecimalScale
                      onChange={handleProductChange('msrp')}
                    />
                  </div>
                </div>
              </div>
              <div className="col-sm-4">
                <span style={{ fontWeight: 700 }}>Price</span>
                <div style={{ marginTop: 10, marginBottom: 10 }}>
                  <div className="input-group">
                    <span className="input-group-text">$</span>
                    <NumberFormat
                      className="form-control"
                      thousandsGroupStyle="thousand"
                      value={state.selectedProduct.sale_price}
                      decimalSeparator="."
                      displayType="input"
                      type="text"
                      thousandSeparator
                      allowNegative={false}
                      decimalScale={2}
                      fixedDecimalScale
                      onChange={handleProductChange('sale_price')}
                    />
                  </div>
                  <span className="text-danger text-sm-start">
                    {validationErrors.sale_price}
                  </span>
                </div>
              </div>
              <div className="col-sm-4">
                <span style={{ fontWeight: 700 }}>Cost</span>
                <div style={{ marginTop: 10, marginBottom: 10 }}>
                  <div className="input-group">
                    <span className="input-group-text">$</span>
                    <NumberFormat
                      className="form-control"
                      thousandsGroupStyle="thousand"
                      value={state.selectedProduct.cost_price}
                      decimalSeparator="."
                      displayType="input"
                      type="text"
                      thousandSeparator
                      allowNegative={false}
                      decimalScale={2}
                      fixedDecimalScale
                      onChange={handleProductChange('cost_price')}
                    />
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>
        <div className="col-md-4">
          <Card style={{ padding: 10 }}>
            <span style={{ fontWeight: 700 }}>Shop</span>
            <div style={{ marginTop: 10, marginBottom: 10 }}>
              <AsyncSelect
                value={state.selectedVenue}
                onChange={handleSelectChange('selectedVenue')}
                loadOptions={loadShops}
                isOptionSelected={(option) => state.selectedVenue === option}
              />
              <span className="text-danger text-sm-start">
                {validationErrors.venue_id}
              </span>
            </div>
          </Card>
          <Card style={{ marginTop: 12, padding: 10 }}>
            <div className="row">
              <div
                className={
                  state.selectedPackageType &&
                  (state.selectedPackageType.value === 'Pack' ||
                    state.selectedPackageType.value === 'Box' ||
                    state.selectedPackageType.value === 'Bundle' ||
                    state.selectedPackageType.value === 'Jar' ||
                    state.selectedPackageType.value === 'Tin')
                    ? 'col-md-7'
                    : 'col-md-12'
                }
              >
                <span style={{ fontWeight: 700 }}>Product Type</span>
                <div style={{ marginTop: 10, marginBottom: 10 }}>
                  <AsyncSelect
                    value={state.selectedPackageType}
                    onChange={handleSelectChange('selectedPackageType')}
                    defaultOptions={defaultPackageType.map((value) => ({
                      value,
                      label: value,
                    }))}
                    isOptionSelected={(option) =>
                      state.selectedPackageType === option
                    }
                  />
                  <span className="text-danger text-sm-start">
                    {validationErrors['packaging.type']}
                  </span>
                </div>
              </div>
              <div
                className="col-md-5"
                style={{
                  display:
                    state.selectedPackageType &&
                    (state.selectedPackageType.value === 'Pack' ||
                      state.selectedPackageType.value === 'Box' ||
                      state.selectedPackageType.value === 'Bundle' ||
                      state.selectedPackageType.value === 'Jar' ||
                      state.selectedPackageType.value === 'Tin')
                      ? 'inline'
                      : 'none',
                }}
              >
                <span style={{ fontWeight: 700 }}>Quantity</span>
                <div style={{ marginTop: 10, marginBottom: 10 }}>
                  <input
                    className="form-control"
                    value={state.selectedPackageQuantity}
                    onChange={handleInputChange('selectedPackageQuantity')}
                  />
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-sm-12">
                <span style={{ fontWeight: 700 }}>Tags (coming soon)</span>
                <div style={{ marginTop: 10, marginBottom: 10 }}>
                  <AsyncSelect
                    value={state.selectedCategories}
                    placeholder="Add or select..."
                    onChange={handleSelectChange('selectedCategories')}
                    defaultOptions={defaultCategories.map((value) => ({
                      value,
                      label: value,
                    }))}
                    isMulti
                    isOptionSelected={(option) =>
                      state.selectedCategories === option
                    }
                  />
                </div>
              </div>
            </div>
          </Card>
          {state.selectedPackageType?.value !== 'Accessory' && (
            <Card style={{ marginTop: 12, padding: 10 }}>
              <div className="row">
                <div className="col-sm-12">
                  <div>
                    <span style={{ fontWeight: 700 }}>Linked Cigars</span>
                    <span
                      style={{
                        color: 'rgb(211, 169, 102)',
                        cursor: 'pointer',
                        float: 'right',
                      }}
                      onClick={() => setShowLinkCigarModal(true)}
                    >
                      {'Manage'}
                    </span>
                  </div>
                  <div style={{ marginTop: 10, marginBottom: 10 }}>
                    {state.selectedProduct.contents &&
                    state.selectedProduct.contents.length > 0 ? (
                      <>
                        {state.selectedProduct.contents.map((content) => (
                          <div>
                            {content.quantity || 1} - {content.cigar.full_name}{' '}
                            {content.vitola && content.vitola.formatted_name}
                          </div>
                        ))}
                      </>
                    ) : (
                      <div>No linked cigars</div>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          )}
          <Card style={{ marginTop: 12, padding: 10 }}>
            <span>
              These fields are automatically updated daily. Overriding will only
              last beyond the next sync date for discontinued items (records
              missing from the CSV file).
            </span>
            <div className="row" style={{ marginTop: 10 }}>
              <div className="col-md-7">
                <span style={{ fontWeight: 700 }}>Availability</span>
                <div style={{ marginTop: 10, marginBottom: 10 }}>
                  <Select
                    value={state.selectedAvailability}
                    onChange={(selection) =>
                      setState((prev) => ({
                        ...prev,
                        selectedAvailability: selection,
                      }))
                    }
                    options={[
                      { value: 'In Stock', label: 'In Stock' },
                      { value: 'Out Of Stock', label: 'Out Of Stock' },
                      { value: 'Backordered', label: 'Backordered' },
                      { value: 'Sold Out', label: 'Sold Out' },
                      { value: 'Pre Order', label: 'Pre Order' },
                      { value: 'Pre Sale', label: 'Pre Sale' },
                      { value: 'Discontinued', label: 'Discontinued' },
                    ]}
                    getOptionLabel={(option) => `${option.label}`}
                    getOptionValue={(option) => `${option.value}`}
                    isOptionSelected={(option) =>
                      state.selectedAvailability &&
                      state.selectedAvailability.value === option.value
                    }
                  />
                  <span className="text-danger text-sm-start">
                    {validationErrors.availability}
                  </span>
                </div>
              </div>
              <div className="col-md-5">
                <span style={{ fontWeight: 700 }}>Stock Count</span>
                <div style={{ marginTop: 10, marginBottom: 10 }}>
                  <div className="input-group">
                    <input
                      className="form-control"
                      value={state.selectedProduct.stock_count}
                      onChange={handleProductChange('stock_count')}
                    />
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
      {renderLinkCigarModal()}
      {renderImageFromUrlModal()}
    </>
  );
};

export default ProductEditor;
